<template>
  <div class="container-fluid p-0">
    <div class="row m-0">
      <div class="col-12 p-0">
        <div class="login-card">
          <div>
            <div class="login-main wide-main position-relative">
              <!-- Loading overlay -->
              <div v-if="loading" class="loader-overlay d-flex align-items-center justify-content-center">
                <div class="text-center">
                  <div class="spinner-border text-primary" role="status" aria-label="Loading"></div>
                  <div class="mt-2 fw-semibold text-primary">Please wait...</div>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 gap-sm-3 mb-3">
                <h2 class="mb-0">Vehicle & License </h2>
              </div>

              <!-- Header: Driver Info -->
              <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                <div class="d-flex align-items-center gap-3 flex-wrap">
                  <img
                      :src="`/api/driver/${driverId}/profile-picture`"
                      alt="Driver photo"
                      class="rounded-circle border"
                      style="width: 84px; height: 84px; object-fit: cover;"
                  />

                  <div class="flex-grow-1">
                    <div class="fs-4 f-w-700">{{ driver.name }}</div>
                    <div class="text-muted" v-if="driver.rating !== null">
                      <i class="fa-solid fa-star text-warning"></i>
                      <span class="ms-1">{{ Number(driver.rating).toFixed(1) }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="hasVehicleImage" class="vehicle-image-wrap">
                  <img :src="`/api/driver/${driverId}/vehicle-image`" alt="Vehicle image" class="vehicle-thumb"/>
                </div>
              </div>

              <hr/>

              <button
                  :class="[isEditing ? 'btn-danger' :'btn-primary', 'btn','d-flex', 'align-items-center', 'justify-content-center', 'edit-action-btn', 'mb-3']"
                  @click="toggleEdit"
              >
                <i :class="['me-2', 'fa-solid', isEditing ? 'fa-xmark ' : 'fa-pen']" aria-hidden="true"></i>
                <span>{{ isEditing ? 'Cancel' : 'Edit' }}</span>
              </button>


              <!-- Form: Vehicle & License -->
              <form class="theme-form" @submit.prevent="handleSave">
                <div class="accordion dark-accordion" id="vehicleLicenseAccordion">

                  <!-- Vehicle Section -->
                  <div class="accordion-item accordion-wrapper accordion-left-border">
                    <h2 class="accordion-header" id="vehicleRegHeader">
                      <button class="accordion-button collapsed" type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#vehicle-reg-collapse"
                              aria-expanded="false" aria-controls="vehicle-reg-collapse">
                        <div class="d-flex flex-column align-items-start justify-content-between w-100">
                          <span class="d-flex align-items-center">
                            <i class="fa-solid fa-car me-2"></i>
                            Vehicle
                          </span>

                          <div class="d-flex align-items-center gap-2 mt-2">
                            <template v-if="isEditing">
                              <span class="badge badge-responsive bg-info">Editing</span>
                            </template>
                            <i class="iconly-Arrow-Down-2 icli icon"></i>
                          </div>
                        </div>
                      </button>
                    </h2>
                    <div id="vehicle-reg-collapse" class="accordion-collapse collapse"
                         aria-labelledby="vehicleRegHeader"
                         data-bs-parent="#vehicleLicenseAccordion">
                      <div class="accordion-body">
                        <div class="row gy-4">
                          <!-- Vehicle Info -->
                          <div class="col-12">
                            <h5 class="f-w-600 mb-2">Vehicle Information</h5>
                            <div class="row g-3">
                              <div class="col-md-6">
                                <label class="col-form-label">Make</label>
                                <input class="form-control" type="text" v-model="form.vehicle.make"
                                       :disabled="!isEditing" placeholder="e.g., Toyota"/>
                              </div>
                              <div class="col-md-6">
                                <label class="col-form-label">Model</label>
                                <input class="form-control" type="text" v-model="form.vehicle.model"
                                       :disabled="!isEditing" placeholder="e.g., Corolla"/>
                              </div>
                              <div class="col-md-6">
                                <label class="col-form-label">Year</label>
                                <input class="form-control" type="number" v-model.number="form.vehicle.year"
                                       :disabled="!isEditing" placeholder="e.g., 2018"/>
                              </div>
                              <div class="col-md-6">
                                <label class="col-form-label">Color</label>
                                <input class="form-control" type="text" v-model="form.vehicle.color"
                                       :disabled="!isEditing" placeholder="e.g., Black"/>
                              </div>
                              <div class="col-md-6">
                                <label class="col-form-label">Plate Number</label>
                                <input class="form-control" type="text" v-model="form.vehicle.plateNumber"
                                       :disabled="!isEditing" placeholder="e.g., ABC-1234"/>
                              </div>
                              <div class="col-md-6">
                                <label class="col-form-label">Vehicle Type</label>
                                <select id="vehicle-type-select" class="form-select" @change="onVehicleTypeChange"
                                        :value="form.vehicle.vehicleType && form.vehicle.capacity ? `${form.vehicle.vehicleType}-${form.vehicle.capacity}` : ''"
                                        :disabled="!isEditing">
                                  <option value="" disabled>Select vehicle type</option>
                                  <option v-for="(vt, idx) in vehicleTypeOptions" :key="idx" :value="`${vt.type}-${vt.capacity}`">
                                    {{ vt.label }}
                                  </option>
                                </select>
                                <div class="form-text">Choose from the available vehicle categories.</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- License Section -->
                  <div class="accordion-item accordion-wrapper accordion-left-border">
                    <h2 class="accordion-header" id="licenseHeader">
                      <button class="accordion-button collapsed" type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#license-collapse"
                              aria-expanded="false" aria-controls="license-collapse">

                        <div class="d-flex flex-column align-items-start justify-content-between w-100">
                          <span class="d-flex align-items-center">
                            <i class="fa-solid fa-id-card me-2"></i>
                            License
                          </span>

                          <div class="d-flex align-items-center gap-2 mt-2">
                            <template v-if="!isEditing">
                              <span class="badge badge-responsive" :class="form.license.verified ? 'bg-success' : 'bg-secondary'">
                                {{ form.license.verified ? 'Verified' : 'Not verified' }}
                              </span>
                            </template>
                            <template v-else>
                              <span class="badge badge-responsive bg-info">Editing</span>
                            </template>
                            <i class="iconly-Arrow-Down-2 icli icon"></i>
                          </div>
                        </div>
                      </button>
                    </h2>
                    <div id="license-collapse" class="accordion-collapse collapse" aria-labelledby="licenseHeader"
                         data-bs-parent="#vehicleLicenseAccordion">
                      <div class="accordion-body">
                        <div class="row g-3">
                          <div class="col-md-6">
                            <label class="col-form-label">Expiry Date</label>
                            <input class="form-control" type="date" v-model="form.license.expiryDate"
                                   :disabled="!isEditing"/>
                          </div>

                          <div class="col-12">
                            <label class="col-form-label">License Document</label>
                            <DriverLicenseSidesUpload v-model="licenseSides" :disabled="!isEditing"
                                                      id-prefix="vehicle-license"/>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Insurance Section -->
                  <div class="accordion-item accordion-wrapper accordion-left-border">
                    <h2 class="accordion-header" id="insuranceHeader">
                      <button class="accordion-button collapsed" type="button"
                              data-bs-toggle="collapse"
                              data-bs-target="#insurance-collapse"
                              aria-expanded="false" aria-controls="insurance-collapse">

                        <div class="d-flex flex-column align-items-start justify-content-between w-100">
                          <span class="d-flex align-items-center">
                            <i class="fa-solid fa-shield-halved me-2"></i>
                            Insurance
                          </span>

                          <div class="d-flex align-items-center gap-2 mt-2">
                            <template v-if="!isEditing">
                              <span class="badge badge-responsive" :class="form.insurance.verified ? 'bg-success' : 'bg-secondary'">
                                {{ form.insurance.verified ? 'Verified' : 'Not verified' }}
                              </span>
                            </template>
                            <template v-else>
                              <span class="badge badge-responsive bg-info">Editing</span>
                            </template>
                            <i class="iconly-Arrow-Down-2 icli icon"></i>
                          </div>
                        </div>
                      </button>
                    </h2>
                    <div id="insurance-collapse" class="accordion-collapse collapse" aria-labelledby="insuranceHeader"
                         data-bs-parent="#vehicleLicenseAccordion">
                      <div class="accordion-body">
                        <div class="row g-3">
                          <div class="col-md-6">
                            <label class="col-form-label">Expiry Date</label>
                            <input class="form-control" type="date" v-model="form.insurance.expiryDate"
                                   :disabled="!isEditing"/>
                          </div>

                          <div class="col-12">
                            <label class="col-form-label">Insurance Document</label>
                            <!-- The same two-button picker the licence sides above use, rather
                                 than a click-anywhere drop zone: on a phone the zone is a large
                                 target with no visible affordance for the camera, and this page is
                                 opened from the driver app. The browse control is always present
                                 beside the camera one, which matters in that webview — Android
                                 cannot resolve the capture intent there until
                                 asherRides-driver-app#35 is confirmed on a device. -->
                            <FilePickerControls input-id="vehicle-insurance"
                                                accept="image/*,application/pdf"
                                                :disabled="!isEditing || insurancePreparing"
                                                :camera-label="hasInsuranceOnFile ? 'Retake' : 'Take photo'"
                                                :file-label="hasInsuranceOnFile ? 'Replace' : 'Choose file'"
                                                :sole-label="hasInsuranceOnFile ? 'Replace document' : 'Choose file'"
                                                @picked="handleUpload('insurance', $event)"/>
                            <small v-if="insurancePreparing" class="text-muted d-block mt-2">
                              <span class="spinner-border spinner-border-sm me-1"></span>Preparing document...
                            </small>
                            <div v-else-if="pendingInsuranceFile" class="mt-2">
                              <small class="text-success d-block mb-1">
                                <i class="fa fa-check-circle me-1"></i>File selected — saving will replace the stored document
                              </small>
                              <div class="small text-muted text-truncate" :title="pendingInsuranceFile.name">
                                <i class="fa fa-file-alt me-1"></i>{{ pendingInsuranceFile.name }}
                              </div>
                            </div>
                            <small v-else-if="hasInsuranceDocument" class="text-success d-block mt-2">
                              <i class="fa-solid fa-check-circle me-1"></i>Document uploaded
                            </small>
                            <small v-else class="text-muted d-block mt-2">
                              Images or PDF files only (max 5MB).
                            </small>
                          </div>


                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-end mt-4" v-if="isEditing">
                  <button type="submit" class="btn btn-primary d-inline-flex align-items-center" :disabled="loading">
                    <span v-if="!loading">Save Changes</span>
                    <span v-else class="d-inline-flex align-items-center">
                      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </span>
                  </button>
                </div>

              </form>

              <div v-if="error" class="alert alert-warning mt-3">
                {{ error }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import {computed, reactive, ref, onMounted} from 'vue';
import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import DriverLicenseSidesUpload from './DriverLicenseSidesUpload.vue';
import FilePickerControls from './FilePickerControls.vue';
import {preparePickedFile, IMAGE_OR_PDF_TYPES} from '../fileUploadValidation';

// Props
const props = defineProps({
  driverId: {type: String, required: true},
  token: {type: String, required: true}
});

const driverId = props.driverId;

// Local state
const driver = reactive({
  name: null,
  rating: null,
});

const form = reactive({
  vehicle: {
    make: '',
    model: '',
    year: null,
    color: '',
    plateNumber: '',
    capacity: null,
    vehicleType: '',
  },
  license: {
    verified: null,
    expiryDate: '',
  },
  insurance: {
    verified: null,
    expiryDate: '',
  }
});

const vehicleTypeOptions = [
  { type: 'SEDAN', capacity: 4, label: 'Sedan (4 passengers)' },
  { type: 'SUV', capacity: 6, label: 'SUV (6 passengers)' },
  { type: 'SUV', capacity: 4, label: 'SUV (4 passengers)' },
  { type: 'MINIVAN', capacity: 6, label: 'Minivan (6 passengers)' }
];

const onVehicleTypeChange = (event) => {
  const selectedValue = event.target.value;
  const option = vehicleTypeOptions.find(opt => `${opt.type}-${opt.capacity}` === selectedValue);
  if (option) {
    form.vehicle.vehicleType = option.type;
    form.vehicle.capacity = option.capacity;
  }
};

const hasVehicleImage = ref(false);
const hasLicenseDocument = ref(false);
const hasInsuranceDocument = ref(false);
const pendingInsuranceFile = ref(null);

const hasInsuranceOnFile = computed(() => !!pendingInsuranceFile.value || hasInsuranceDocument.value);

// Shape shared with the onboarding documents step, which owns the front/back upload UI.
const licenseSides = reactive({
  driverLicenseFront: null,
  driverLicenseBack: null,
  hasLicenseDocument: false,
});

function licenseSidesPicked() {
  return (licenseSides.driverLicenseFront ? 1 : 0) + (licenseSides.driverLicenseBack ? 1 : 0);
}

function resetLicenseSides() {
  licenseSides.driverLicenseFront = null;
  licenseSides.driverLicenseBack = null;
  // Restored rather than left false: picking a side clears it, and cancelling must not make an
  // untouched stored document look missing.
  licenseSides.hasLicenseDocument = hasLicenseDocument.value;
}

const isEditing = ref(false);
const loading = ref(false);
const error = ref(null);

// Keep a snapshot of form when entering edit mode so we can revert on cancel
const originalForm = ref(null);

function deepCloneForm(src) {
  return JSON.parse(JSON.stringify({
    vehicle: src.vehicle,
    license: src.license,
    insurance: src.insurance,
  }));
}

function isFormDirty() {
  if (licenseSidesPicked() > 0 || fileUploadMap.insurance) return true;
  if (!originalForm.value) return false;
  const current = deepCloneForm(form);
  return JSON.stringify(current) !== JSON.stringify(originalForm.value);
}

function restoreForm() {
  if (!originalForm.value) return;
  Object.assign(form.vehicle, originalForm.value.vehicle || {});
  Object.assign(form.license, originalForm.value.license || {});
  Object.assign(form.insurance, originalForm.value.insurance || {});
}

function clearPendingUploads() {
  uploadSession.value += 1;
  fileUploadMap.insurance = null;
  pendingInsuranceFile.value = null;
  resetLicenseSides();
}

function toggleEdit() {
  if (!isEditing.value) {
    // entering edit mode: snapshot current form
    originalForm.value = deepCloneForm(form);
    error.value = null;
  } else {
    // cancelling edit: if dirty, restore
    if (isFormDirty()) {
      restoreForm();
      clearPendingUploads();
    }
  }
  isEditing.value = !isEditing.value;
}

const insurancePreparing = ref(false);
// Bumped by clearPendingUploads so a compression still in flight can tell its edit is over.
const uploadSession = ref(0);
const fileUploadMap = reactive({
  insurance: null,
});

/**
 * Accepts a picked document, downscaling it first when it is a photo.
 *
 * @param {string} type the document being picked; only 'insurance' is handled here
 * @param {Event} event the change event from the file input
 */
async function handleUpload(type, event) {
  const picked = event?.target?.files?.[0];
  if (!picked || type !== 'insurance') return;

  // Captured before the await: cancelling the edit while a large photo is still compressing would
  // otherwise let the resolved file reappear after clearPendingUploads had already discarded it.
  const pickedDuring = uploadSession.value;
  insurancePreparing.value = true;
  try {
    const file = await preparePickedFile(picked, event, IMAGE_OR_PDF_TYPES,
        {subject: 'insurance document'});
    if (!file || pickedDuring !== uploadSession.value) return;

    fileUploadMap.insurance = file;
    pendingInsuranceFile.value = file;
  } finally {
    insurancePreparing.value = false;
    // Cleared unconditionally so re-picking the same file still raises a change event, matching
    // the other pick handlers.
    if (event.target) event.target.value = '';
  }
}


async function fetchDetails() {
  loading.value = true;
  try {
    const res = await fetch(`/api/driver-vehicle-license?token=${encodeURIComponent(props.token)}`);
    if (!res.ok) throw new Error('Failed to load vehicle & license details.');
    const payload = await res.json();

    // Header
    driver.name = payload.name || null;
    driver.rating = payload.rating ?? null;

    // License
    form.license.verified = payload.licenseDocumentVerified ?? null;
    form.license.expiryDate = payload.licenseExpirationDate || '';
    hasLicenseDocument.value = !!payload.hasLicenseDocument;
    resetLicenseSides();

    // Vehicle
    form.vehicle.make = payload.make || '';
    form.vehicle.model = payload.model || '';
    form.vehicle.year = payload.year ?? null;
    form.vehicle.color = payload.color || '';
    form.vehicle.plateNumber = payload.plateNumber || '';
    form.vehicle.vehicleType = payload.vehicleType || '';
    form.vehicle.capacity = payload.capacity ?? null;
    hasVehicleImage.value = !!payload.hasVehicleImage;

    // Insurance
    form.insurance.verified = payload.insuranceDocumentVerified ?? null;
    form.insurance.expiryDate = payload.insuranceDocumentExpiryDate || '';
    hasInsuranceDocument.value = !!payload.hasInsuranceDocument;
  } catch (e) {
    error.value = e.message || 'Unable to load details.';
  } finally {
    loading.value = false;
  }
}


// Save handler: validates, confirms, and posts multipart payload to backend
async function handleSave() {
  if (loading.value) return;
  if (insurancePreparing.value) {
    // Saving now would post without the document the driver just picked, and tell them it worked.
    error.value = 'Your document is still being prepared. Please wait a moment and try again.';
    return;
  }

  // Primary validations
  const errs = [];
  const requiredFields = [
    {path: 'Vehicle Make', v: form.vehicle.make},
    {path: 'Vehicle Model', v: form.vehicle.model},
    {path: 'Vehicle Year', v: form.vehicle.year},
    {path: 'Vehicle Color', v: form.vehicle.color},
    {path: 'Plate Number', v: form.vehicle.plateNumber},
    {path: 'Vehicle Type', v: form.vehicle.vehicleType},
    {path: 'License Expiry Date', v: form.license.expiryDate},
  ];
  requiredFields.forEach(f => {
    if (f.v === null || f.v === undefined || String(f.v).trim() === '') errs.push(`${f.path} is required.`);
  });

  const currentYear = new Date().getFullYear() + 1; // allow next year for new models
  if (form.vehicle.year && (form.vehicle.year < 1980 || form.vehicle.year > currentYear)) {
    errs.push(`Vehicle Year must be between 1980 and ${currentYear}.`);
  }
  if (form.vehicle.capacity && (form.vehicle.capacity <= 0 || form.vehicle.capacity > 12)) {
    errs.push('Invalid vehicle capacity.');
  }
  // A licence is stored as one document collated from both sides, so one side alone cannot replace it.
  if (licenseSidesPicked() === 1) {
    errs.push('Both the front and back of your driver\'s license are required.');
  }
  // License expiry should not be in the past
  if (form.license.expiryDate) {
    const exp = new Date(form.license.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (exp < today) errs.push('License expiry date cannot be in the past.');
  }

  if (errs.length) {
    error.value = errs.join(' ');
    Swal.fire({
      title: 'Please fix the following',
      html: errs.map(e => `<div>${e}</div>`).join(''),
      icon: 'warning'
    });
    return;
  }

  // Confirmation prompt
  const resConfirm = await Swal.fire({
    title: 'Confirm update',
    text: 'You may not be able to accept rides until your updates are verified. Continue?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, continue',
    cancelButtonText: 'Cancel'
  });
  if (!resConfirm.isConfirmed) return;

  loading.value = true;
  error.value = null;

  try {
    const payload = {
      vehicle: {...form.vehicle},
      license: {...form.license},
      insurance: {...form.insurance},
    };

    const fd = new FormData();
    fd.append('payload', new Blob([JSON.stringify(payload)], {type: 'application/json'}));
    if (licenseSides.driverLicenseFront) fd.append('driverLicenseFile[0]', licenseSides.driverLicenseFront);
    if (licenseSides.driverLicenseBack) fd.append('driverLicenseFile[1]', licenseSides.driverLicenseBack);
    if (fileUploadMap.insurance) fd.append('insuranceDocument', fileUploadMap.insurance);

    const res = await fetch(`/api/driver-vehicle-license?token=${encodeURIComponent(props.token)}`, {
      method: 'POST',
      body: fd
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Failed to save changes.');
    }

    Swal.fire({title: 'Saved', text: 'Your updates have been submitted for review.', icon: 'success'});

    isEditing.value = false;
    fileUploadMap.insurance = null;
    pendingInsuranceFile.value = null;
    licenseSides.driverLicenseFront = null;
    licenseSides.driverLicenseBack = null;
    await fetchDetails();
  } catch (e) {
    error.value = e.message || 'Unable to save changes.';
    Swal.fire({title: 'Error', text: error.value, icon: 'error'});
  } finally {
    loading.value = false;
  }
}

onMounted(fetchDetails);
</script>

<style scoped>
.edit-action-btn {
  min-width: 100px;
}

@media (max-width: 575.98px) {
  .edit-action-btn {
    min-width: 0;
  }
}

/* General mobile improvements */
@media (max-width: 575.98px) {
  .form-control {
    font-size: 16px; /* Prevents zoom on iOS */
  }

  .btn {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
}
.loader-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  z-index: 10;
}

.vehicle-image-wrap{
  flex: 0 0 auto;
}

.vehicle-thumb {
  width: 160px;
  height: 120px; /* Increased from 100px to better fit square images */
  object-fit: contain;
}

/* The theme pins .login-main to a fixed width — 90vw below 576px — and adds 20px of its own padding
   inside .login-card's 12px, so a 375px phone renders this page in a 298px column with the login
   background showing down both sides. `max-width` alone cannot undo an explicit `width`. This page
   is opened from the driver app, where a sheet that fills the webview is what the surrounding
   chrome looks like. */
@media (max-width: 575.98px) {
  /* Block rather than flex: the unclassed wrapper between .login-card and .login-main is a flex
     item, so it shrinks to its content and a full-width .login-main resolves against that instead
     of the viewport. */
  .login-card {
    display: block;
    padding: 0;
  }

  .login-card > div {
    width: 100%;
  }

  .login-card .login-main.wide-main {
    width: 100%;
    max-width: none;
    min-height: 100vh;
    padding: 16px 14px;
    border-radius: 0;
    box-shadow: none;
  }
}

</style>
